import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { TaskProgressWebSocketService } from '../../services/task-progress-websocket.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { A11yModule } from '@angular/cdk/a11y';
import { AdminService, DiscoveryCitation, DiscoveryCitationListResponse } from '../../services/admin.service';
import { GenericTemplateCommonService } from '../../shared/components/template-ai-common/services/generic-template-common.service';
import { PLATFORM_LABELS } from '../../shared/models/top-prompt-by-platform.model';

@Component({
  selector: 'app-discovery-citations-management',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
  ],
  templateUrl: './discovery-citations-management.component.html',
  styleUrl: './discovery-citations-management.component.scss'
})
export class DiscoveryCitationsManagementComponent implements OnInit, OnDestroy {
  private adminService = inject(AdminService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private taskProgressWs = inject(TaskProgressWebSocketService);
  private templateCommon = inject(GenericTemplateCommonService);
  private cdr = inject(ChangeDetectorRef);

  /** Same browser session: analysis bulk / LLM visibility task IDs (parallel to competitor discovery). */
  analysisBulkTaskId: string | null = null;
  analysisLlmTaskId: string | null = null;

  /** Admin stopped competitor discovery load for this citation (session); cleared on manual reload. */
  competitorLoadStoppedIds = new Set<number>();
  private competitorPollTimers = new Map<number, ReturnType<typeof setInterval>>();
  /** Expanded panel citation IDs — WebSocket refreshes detail only for these. */
  private expandedCitationIds = new Set<number>();

  /** Per citation row index: full response shown (vs 2–3 line preview). Key `${citationId}-${index}`. */
  private discoveryResponseFullExpanded = new Set<string>();
  private competitorDiscoveryWsSub: Subscription | null = null;
  private analysisTaskSubs: Subscription[] = [];

  citations: DiscoveryCitation[] = [];
  totalCount = 0;
  totalPages = 0;
  currentPage = 1;
  pageSize = 50;
  loading = false;
  citationDetailsMap: Map<number, any> = new Map();
  loadingDetailId: number | null = null;

  /** Citation for which the Export menu is open (set when opening menu). */
  exportingCitation: DiscoveryCitation | null = null;

  /** Filter list by primary brand or competitor name (admin). */
  searchTerm = '';

  ngOnInit(): void {
    this.loadCitations();
    this.taskProgressWs.connect();
    this.analysisTaskSubs.push(
      this.templateCommon.currentTaskId$.subscribe((id) => {
        this.analysisBulkTaskId = id;
      }),
      this.templateCommon.llmComparisonMetricsTaskId$.subscribe((id) => {
        this.analysisLlmTaskId = id;
      })
    );
    this.competitorDiscoveryWsSub = this.taskProgressWs
      .messagesByPath('discovery_citation_competitor')
      .subscribe((msg) => {
        const id = Number((msg.data as Record<string, unknown>)?.['citation_id']);
        if (!Number.isFinite(id) || !this.expandedCitationIds.has(id)) {
          return;
        }
        this.adminService.getDiscoveryCitationDetail(id).subscribe({
          next: (d) => {
            this.citationDetailsMap.set(id, d);
            const st = String((msg.data as Record<string, unknown>)?.['status'] ?? '');
            if (st === 'completed') {
              this.snackBar.open('Competitor discovery data loaded.', 'Close', { duration: 4000 });
            }
            if (['completed', 'cancelled', 'error'].includes(st)) {
              this.stopCompetitorPolling(id);
            }
          }
        });
      });
  }

  ngOnDestroy(): void {
    for (const id of this.competitorPollTimers.keys()) {
      this.stopCompetitorPolling(id);
    }
    this.competitorDiscoveryWsSub?.unsubscribe();
    this.analysisTaskSubs.forEach((s) => s.unsubscribe());
    this.analysisTaskSubs = [];
    this.expandedCitationIds.clear();
  }

  loadCitations(): void {
    this.loading = true;
    const skip = (this.currentPage - 1) * this.pageSize;

    this.adminService.getDiscoveryCitations(skip, this.pageSize, this.searchTerm || undefined).subscribe({
      next: (response: DiscoveryCitationListResponse) => {
        this.citations = response.citations;
        this.totalCount = response.total;
        this.totalPages = Math.ceil(response.total / this.pageSize) || 1;
        this.loading = false;
        this.citationDetailsMap.clear();
      },
      error: (err) => {
        console.error('Failed to load discovery citations:', err);
        this.snackBar.open(
          err.error?.detail || err.error?.message || 'Failed to load discovery citations',
          'Close',
          { duration: 5000 }
        );
        this.loading = false;
      }
    });
  }

  deleteCitation(citation: DiscoveryCitation): void {
    const dialogRef = this.dialog.open(ConfirmDeleteCitationDialogComponent, {
      width: '420px',
      disableClose: true,
      data: {
        brand: citation.brand,
        category: citation.category,
        region: citation.region
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.adminService.deleteDiscoveryCitation(citation.id).subscribe({
          next: () => {
            this.snackBar.open('Discovery citation deleted successfully', 'Close', { duration: 3000 });
            this.loadCitations();
          },
          error: (err) => {
            this.snackBar.open(
              err.error?.detail || err.error?.message || 'Failed to delete discovery citation',
              'Close',
              { duration: 5000 }
            );
          }
        });
      }
    });
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadCitations();
    }
  }

  /** MatExpansionPanel expandedChange emits true when opened, false when closed (CDK accordion). */
  onPanelToggle(citation: DiscoveryCitation, opened: boolean): void {
    if (!opened) {
      this.expandedCitationIds.delete(citation.id);
      this.stopCompetitorPolling(citation.id);
      return;
    }
    this.expandedCitationIds.add(citation.id);
    // Always load full detail so admin gets discovery_sections (competitor prompt counts, MG, etc.)
    this.loadingDetailId = citation.id;
    this.adminService.getDiscoveryCitationDetail(citation.id).subscribe({
      next: (detail) => {
        this.citationDetailsMap.set(citation.id, detail);
        this.loadingDetailId = null;
        this.afterDiscoveryDetailLoaded(citation, detail);
      },
      error: () => { this.loadingDetailId = null; }
    });
  }

  private getCompetitorNamesForCitation(citation: DiscoveryCitation, citationId: number): string[] {
    const fromList = citation.competitors_in_run?.filter(Boolean).map((s) => String(s).trim()) ?? [];
    if (fromList.length) return [...new Set(fromList)];
    const entries = this.competitorDiscoveryEntries(citationId);
    return entries.map(([k]) => k).filter(Boolean);
  }

  /** All listed competitors have zero AI Mode prompt rows (needs backfill). */
  allCompetitorDiscoveryPromptsEmpty(citation: DiscoveryCitation, citationId: number): boolean {
    const names = this.getCompetitorNamesForCitation(citation, citationId);
    if (!names.length) return false;
    const ds = this.getDiscoverySections(citationId);
    const comp = (ds?.['competitors'] as Record<string, unknown[]>) || {};
    return names.every((n) => !Array.isArray(comp[n]) || comp[n].length === 0);
  }

  private competitorBatchMeta(detail: { discovery_sections?: unknown }): Record<string, unknown> | null {
    const ds = detail?.discovery_sections;
    if (!ds || typeof ds !== 'object') return null;
    const meta = (ds as Record<string, unknown>)['metadata'];
    if (!meta || typeof meta !== 'object') return null;
    const batch = (meta as Record<string, unknown>)['_competitor_batch'];
    return batch && typeof batch === 'object' ? (batch as Record<string, unknown>) : null;
  }

  isStaleCompetitorRunning(detail: { competitor_job_active?: boolean; discovery_sections?: unknown }): boolean {
    const batch = this.competitorBatchMeta(detail);
    return batch?.['status'] === 'running' && detail.competitor_job_active !== true;
  }

  isCompetitorJobActivelyRunning(detail: { competitor_job_active?: boolean; discovery_sections?: unknown }): boolean {
    if (detail.competitor_job_active === true) return true;
    if (this.isStaleCompetitorRunning(detail)) return false;
    return this.competitorBatchMeta(detail)?.['status'] === 'running';
  }

  private afterDiscoveryDetailLoaded(citation: DiscoveryCitation, detail: { competitor_job_active?: boolean; discovery_sections?: unknown }): void {
    const id = citation.id;
    const names = this.getCompetitorNamesForCitation(citation, id);
    if (!names.length) return;

    if (this.isStaleCompetitorRunning(detail)) {
      return;
    }

    if (detail.competitor_job_active === true) {
      if (!this.taskProgressWs.useWebSocket) {
        this.startCompetitorPolling(id);
      }
      return;
    }

    if (!this.allCompetitorDiscoveryPromptsEmpty(citation, id)) {
      return;
    }

    if (this.competitorLoadStoppedIds.has(id)) {
      return;
    }

    const batch = this.competitorBatchMeta(detail);
    if (batch?.['status'] === 'running') {
      return;
    }

    this.adminService.startCompetitorDiscovery(id).subscribe({
      next: () => {
        this.adminService.getDiscoveryCitationDetail(id).subscribe({
          next: (d) => {
            this.citationDetailsMap.set(id, d);
            if (!this.taskProgressWs.useWebSocket) {
              this.startCompetitorPolling(id);
            }
          },
          error: () => {
            if (!this.taskProgressWs.useWebSocket) {
              this.startCompetitorPolling(id);
            }
          }
        });
      },
      error: (err) => {
        this.snackBar.open(
          err.error?.detail || err.error?.message || 'Could not start competitor discovery load',
          'Close',
          { duration: 6000 }
        );
      }
    });
  }

  startCompetitorPolling(citationId: number): void {
    if (this.competitorPollTimers.has(citationId)) return;
    const timer = setInterval(() => {
      this.adminService.getDiscoveryCitationDetail(citationId).subscribe({
        next: (d) => {
          this.citationDetailsMap.set(citationId, d);
          if (d.competitor_job_active === true) return;
          const batch = this.competitorBatchMeta(d);
          const st = String(batch?.['status'] ?? '');
          if (['completed', 'cancelled', 'error'].includes(st)) {
            if (st === 'completed') {
              this.snackBar.open('Competitor discovery data loaded.', 'Close', { duration: 4000 });
            }
            this.stopCompetitorPolling(citationId);
            return;
          }
          if (st === 'running') {
            this.stopCompetitorPolling(citationId);
          }
        },
        error: () => this.stopCompetitorPolling(citationId)
      });
    }, 2500);
    this.competitorPollTimers.set(citationId, timer);
  }

  stopCompetitorPolling(citationId: number): void {
    const t = this.competitorPollTimers.get(citationId);
    if (t) {
      clearInterval(t);
      this.competitorPollTimers.delete(citationId);
    }
  }

  stopCompetitorLoad(citation: DiscoveryCitation): void {
    this.competitorLoadStoppedIds.add(citation.id);
    this.adminService.cancelCompetitorDiscovery(citation.id).subscribe({
      next: () => {
        this.stopCompetitorPolling(citation.id);
        this.adminService.getDiscoveryCitationDetail(citation.id).subscribe({
          next: (d) => this.citationDetailsMap.set(citation.id, d)
        });
        this.snackBar.open('Competitor load stopped. You can resume when ready.', 'Close', { duration: 4000 });
      },
      error: () => {
        this.stopCompetitorPolling(citation.id);
        this.snackBar.open('Stop requested.', 'Close', { duration: 3000 });
      }
    });
  }

  /** Manual reload: competitor discovery batch (same as bulk overview). */
  loadCompetitorDiscoveryData(citation: DiscoveryCitation): void {
    this.competitorLoadStoppedIds.delete(citation.id);
    this.adminService.startCompetitorDiscovery(citation.id).subscribe({
      next: (r) => {
        if (r.status === 'already_running') {
          this.snackBar.open('Competitor load already in progress.', 'Close', { duration: 3000 });
        }
        this.adminService.getDiscoveryCitationDetail(citation.id).subscribe({
          next: (d) => {
            this.citationDetailsMap.set(citation.id, d);
            if (!this.taskProgressWs.useWebSocket) {
              this.startCompetitorPolling(citation.id);
            }
          }
        });
      },
      error: (err) => {
        this.snackBar.open(
          err.error?.detail || err.error?.message || 'Could not start competitor discovery',
          'Close',
          { duration: 6000 }
        );
      }
    });
  }

  showCompetitorResumeRow(citation: DiscoveryCitation): boolean {
    const d = this.citationDetailsMap.get(citation.id);
    if (!d) return false;
    if (this.isCompetitorJobActivelyRunning(d)) return false;
    if (this.isStaleCompetitorRunning(d)) return true;
    if (this.competitorLoadStoppedIds.has(citation.id)) return true;
    const batch = this.competitorBatchMeta(d);
    if (batch?.['status'] === 'cancelled' || batch?.['status'] === 'error') return true;
    return false;
  }

  showCompetitorStopButton(citation: DiscoveryCitation): boolean {
    const d = this.citationDetailsMap.get(citation.id);
    return !!d && d.competitor_job_active === true;
  }

  applySearch(): void {
    this.currentPage = 1;
    this.loadCitations();
  }

  getDiscoverySections(id: number): Record<string, unknown> | null {
    const d = this.citationDetailsMap.get(id);
    const ds = d?.discovery_sections;
    return ds && typeof ds === 'object' ? ds as Record<string, unknown> : null;
  }

  competitorDiscoveryEntries(id: number): [string, unknown[]][] {
    const ds = this.getDiscoverySections(id);
    const c = ds?.['competitors'];
    if (!c || typeof c !== 'object') return [];
    return Object.entries(c as Record<string, unknown[]>).map(([k, v]) => [k, Array.isArray(v) ? v : []]);
  }

  getCitationDetail(id: number): any {
    return this.citationDetailsMap.get(id);
  }

  private brandForCitationId(id: number): string {
    return this.citations.find((c) => c.id === id)?.brand ?? '';
  }

  /**
   * Bulk overview stores citation_data as an array of rows (one per brand).
   * Admin rows are per-brand; use the matching row, not citation_data[0].
   */
  private metricsEntryForDetail(detail: any, brand: string): any | null {
    const cd = detail?.citation_data;
    if (cd == null) return null;
    if (Array.isArray(cd)) {
      const b = (brand || '').trim().toLowerCase();
      const hit = cd.find(
        (e: any) =>
          e &&
          typeof e === 'object' &&
          (e.brand || '').trim().toLowerCase() === b
      );
      const first = cd[0];
      return hit ?? (first && typeof first === 'object' ? first : null);
    }
    if (typeof cd === 'object' && cd.ai_visibility_metrics) return cd;
    return null;
  }

  private aiVisibilityMetrics(id: number): any {
    const detail = this.citationDetailsMap.get(id);
    const brand = this.brandForCitationId(id);
    return this.metricsEntryForDetail(detail, brand)?.ai_visibility_metrics ?? null;
  }

  getCitedPages(id: number): any[] {
    const m = this.aiVisibilityMetrics(id);
    const list = m?.cited_pages_list ?? m?.cited_pages;
    return Array.isArray(list) ? list : [];
  }

  /** Official site host from API only — never derive from Serp citations (wrong e.g. autoexpress.co.uk). */
  // displayBaseDomain(citation: DiscoveryCitation, _id: number): string {
  //   return (citation.base_domain || '').trim();
  // }

  /** Per-page prompts: legacy string[] `queries` or API `prompts[]` with { query, platform, ... }. */
  getPagePromptStrings(page: any): string[] {
    if (!page || typeof page !== 'object') return [];
    const out: string[] = [];
    if (Array.isArray(page.queries)) {
      for (const q of page.queries) {
        if (typeof q === 'string' && q.trim()) out.push(q.trim());
      }
    }
    if (Array.isArray(page.prompts)) {
      for (const p of page.prompts) {
        const q =
          typeof p === 'string' ? p : (p?.query ?? p?.prompt ?? '');
        if (typeof q === 'string' && q.trim()) out.push(q.trim());
      }
    }
    return [...new Set(out)];
  }

  /** Collect all unique query strings from cited_pages_list. */
  private collectQueriesFromCitedPages(id: number): Set<string> {
    const fromCited = new Set<string>();
    for (const page of this.getCitedPages(id)) {
      for (const q of this.getPagePromptStrings(page)) {
        fromCited.add(q);
      }
    }
    return fromCited;
  }

  getQueries(id: number): string[] | null {
    const detail = this.citationDetailsMap.get(id);
    const brand = this.brandForCitationId(id);
    const entry = this.metricsEntryForDetail(detail, brand);
    if (!entry) return null;
    const meta = entry?.ai_visibility_metrics?.metadata;
    const queries = meta?.queries;
    // Prefer metadata.queries; if it has only one item, merge with cited_pages_list so "Prompts / Queries used" shows full list
    const fromCited = this.collectQueriesFromCitedPages(id);
    if (Array.isArray(queries) && queries.length > 0) {
      const combined = new Set<string>(queries.filter((q: unknown) => typeof q === 'string' && (q as string).trim()));
      fromCited.forEach((q) => combined.add(q));
      return Array.from(combined);
    }
    // Fallback 1: collect unique query strings from platform_breakdown
    const breakdown = entry?.ai_visibility_metrics?.platform_breakdown;
    if (breakdown && typeof breakdown === 'object') {
      const platformKeys = ['chatgpt', 'gemini', 'ai_overview', 'ai_mode'];
      for (const platform of platformKeys) {
        const list = Array.isArray(breakdown[platform]?.query_results) ? breakdown[platform].query_results : [];
        for (const row of list) {
          const q = row?.query ?? row?.prompt ?? '';
          if (q && typeof q === 'string') fromCited.add(q);
        }
      }
    }
    if (fromCited.size > 0) return Array.from(fromCited);
    return null;
  }

  private normalizePromptQ(q: string): string {
    return (q || '').trim().replace(/\s+/g, ' ');
  }

  private promptFromDiscoveryItem(item: unknown): string {
    if (typeof item === 'string') {
      return item.trim();
    }
    if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>;
      return String(o['prompt'] ?? o['query'] ?? '').trim();
    }
    return '';
  }

  private collectDiscoveryPromptSet(arr: unknown): Set<string> {
    const s = new Set<string>();
    if (!Array.isArray(arr)) {
      return s;
    }
    for (const item of arr) {
      const p = this.promptFromDiscoveryItem(item);
      if (p) {
        s.add(this.normalizePromptQ(p));
      }
    }
    return s;
  }

  /**
   * Classify prompt as Generic / Branded / Competitor using discovery_sections, then row metadata.
   */
  classifyPromptForDiscovery(
    citationId: number,
    query: string,
    row?: { is_branded?: boolean; prompt_type?: string; prompt_scope?: string }
  ): { kindLabel: string; competitorName: string | null } {
    const nq = this.normalizePromptQ(query);
    const ds = this.getDiscoverySections(citationId);
    if (ds && nq) {
      if (this.collectDiscoveryPromptSet(ds['generic']).has(nq)) {
        return { kindLabel: 'Generic', competitorName: null };
      }
      if (this.collectDiscoveryPromptSet(ds['primary_branded']).has(nq)) {
        return { kindLabel: 'Branded', competitorName: null };
      }
      const comps = ds['competitors'];
      if (comps && typeof comps === 'object') {
        for (const [name, rows] of Object.entries(comps as Record<string, unknown[]>)) {
          if (this.collectDiscoveryPromptSet(rows).has(nq)) {
            return { kindLabel: 'Competitor', competitorName: name };
          }
        }
      }
    }
    if (row?.is_branded) {
      return { kindLabel: 'Branded', competitorName: null };
    }
    const scope = String(row?.prompt_scope || '').toLowerCase();
    if (scope === 'non_branded' || String(row?.prompt_type || '').toLowerCase().includes('generic')) {
      return { kindLabel: 'Generic', competitorName: null };
    }
    return { kindLabel: 'Prompt', competitorName: null };
  }

  private iterPlatformQueryResults(id: number): Array<{
    platform: string;
    query: string;
    answer_text: string;
    mentioned_brands: string[];
    citations: any[];
    is_branded?: boolean;
    prompt_type?: string;
    prompt_scope?: string;
  }> {
    const detail = this.citationDetailsMap.get(id);
    const brand = this.brandForCitationId(id);
    const entry = this.metricsEntryForDetail(detail, brand);
    const breakdown = entry?.ai_visibility_metrics?.platform_breakdown;
    if (!breakdown) {
      return [];
    }
    const platformKeys = ['chatgpt', 'gemini', 'ai_overview', 'ai_mode'];
    const flat: Array<{
      platform: string;
      query: string;
      answer_text: string;
      mentioned_brands: string[];
      citations: any[];
      is_branded?: boolean;
      prompt_type?: string;
      prompt_scope?: string;
    }> = [];
    for (const platform of platformKeys) {
      const platData = breakdown[platform];
      const list = Array.isArray(platData?.query_results) ? platData.query_results : [];
      for (const row of list) {
        const query = row?.query ?? row?.prompt ?? '';
        const answer_text = row?.answer_text ?? row?.response ?? '';
        if (!query && !answer_text) {
          continue;
        }
        flat.push({
          platform,
          query,
          answer_text: answer_text || '',
          mentioned_brands: Array.isArray(row?.mentioned_brands) ? row.mentioned_brands : [],
          citations: Array.isArray(row?.citations) ? row.citations : [],
          is_branded: row?.is_branded,
          prompt_type: row?.prompt_type,
          prompt_scope: row?.prompt_scope
        });
      }
    }
    return flat;
  }

  /**
   * Build prompt + response list from stored platform_breakdown (same data as Overview "AI visibility – Top prompts").
   * Used for export and legacy callers.
   */
  getPromptResponses(id: number): Array<{ platform: string; query: string; answer_text: string; mentioned_brands: string[] }> {
    return this.iterPlatformQueryResults(id).map(
      ({ platform, query, answer_text, mentioned_brands }) => ({
        platform,
        query,
        answer_text,
        mentioned_brands
      })
    );
  }

  private hostnameFromUrl(url: string): string {
    try {
      const u = url.includes('://') ? new URL(url) : new URL('https://' + url);
      return u.hostname.replace(/^www\./, '') || url.slice(0, 24);
    } catch {
      return (url || '').slice(0, 32);
    }
  }

  private buildCitedUrlsByQuery(id: number): Map<string, { url: string; title: string }[]> {
    const m = new Map<string, { url: string; title: string }[]>();
    for (const page of this.getCitedPages(id)) {
      const url = String(page?.url || '').trim();
      if (!url) {
        continue;
      }
      const title = String(page?.title || page?.domain || this.hostnameFromUrl(url)).trim() || this.hostnameFromUrl(url);
      const chip = { url, title: title.slice(0, 72) };
      for (const q of this.getPagePromptStrings(page)) {
        const k = this.normalizePromptQ(q);
        if (!k) {
          continue;
        }
        const arr = m.get(k) || [];
        if (!arr.some((x) => x.url === url)) {
          arr.push(chip);
        }
        m.set(k, arr);
      }
    }
    return m;
  }

  private mergeCitationChips(
    rawCitations: any[],
    fromPages: { url: string; title: string }[]
  ): { url: string; title: string; chipIndex: number }[] {
    const out: { url: string; title: string }[] = [];
    const seen = new Set<string>();
    const push = (url: string, title: string) => {
      const u = String(url || '').trim();
      if (!u || seen.has(u)) {
        return;
      }
      seen.add(u);
      out.push({ url: u, title: (title || this.hostnameFromUrl(u)).slice(0, 72) });
    };
    for (const c of rawCitations || []) {
      const url = typeof c === 'string' ? c : String(c?.url || c?.link || c?.href || '').trim();
      const title =
        typeof c === 'string'
          ? this.hostnameFromUrl(c)
          : String(c?.title || c?.name || c?.snippet || this.hostnameFromUrl(url)).trim();
      push(url, title);
    }
    for (const x of fromPages || []) {
      push(x.url, x.title);
    }
    return out.map((c, i) => ({ ...c, chipIndex: i + 1 }));
  }

  responsePreviewLines(text: string, maxLines = 3, maxChars = 240): { preview: string; needsMore: boolean } {
    const t = (text || '').trim();
    if (!t) {
      return { preview: '', needsMore: false };
    }
    const lines = t.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length > maxLines) {
      return { preview: lines.slice(0, maxLines).join('\n'), needsMore: true };
    }
    const joined = lines.join('\n');
    const body = joined || t;
    if (body.length > maxChars) {
      return { preview: body.slice(0, maxChars).trim() + '…', needsMore: true };
    }
    return { preview: body, needsMore: false };
  }

  isDiscoveryResponseFull(citationId: number, index: number): boolean {
    return this.discoveryResponseFullExpanded.has(`${citationId}-${index}`);
  }

  setDiscoveryResponseFull(citationId: number, index: number, full: boolean): void {
    const k = `${citationId}-${index}`;
    if (full) {
      this.discoveryResponseFullExpanded.add(k);
    } else {
      this.discoveryResponseFullExpanded.delete(k);
    }
    this.cdr.markForCheck();
  }

  /**
   * Unified accordion rows: prompts + responses + citation chips (admin Discovery Citations).
   */
  getDiscoveryPromptAccordionItems(id: number): Array<{
    trackKey: string;
    accordionIndex: number;
    platform: string;
    platformLabel: string;
    query: string;
    answer_text: string;
    kindLabel: string;
    competitorName: string | null;
    mentioned_brands: string[];
    citations: { url: string; title: string; chipIndex: number }[];
    preview: string;
    needsMore: boolean;
  }> {
    const citedByQuery = this.buildCitedUrlsByQuery(id);
    const coveredQueries = new Set<string>();
    const out: Array<{
      trackKey: string;
      accordionIndex: number;
      platform: string;
      platformLabel: string;
      query: string;
      answer_text: string;
      kindLabel: string;
      competitorName: string | null;
      mentioned_brands: string[];
      citations: { url: string; title: string; chipIndex: number }[];
      preview: string;
      needsMore: boolean;
    }> = [];

    let idx = 0;
    for (const row of this.iterPlatformQueryResults(id)) {
      const { kindLabel, competitorName } = this.classifyPromptForDiscovery(id, row.query, row);
      const fromPages = citedByQuery.get(this.normalizePromptQ(row.query)) || [];
      const citations = this.mergeCitationChips(row.citations, fromPages);
      const { preview, needsMore } = this.responsePreviewLines(row.answer_text);
      const nq = this.normalizePromptQ(row.query);
      if (nq) {
        coveredQueries.add(nq);
      }
      const ai = idx++;
      out.push({
        trackKey: `${id}-${ai}`,
        accordionIndex: ai,
        platform: row.platform,
        platformLabel: this.getPlatformLabel(row.platform),
        query: row.query,
        answer_text: row.answer_text,
        kindLabel,
        competitorName,
        mentioned_brands: row.mentioned_brands,
        citations,
        preview,
        needsMore: needsMore || row.answer_text.length > preview.length
      });
    }

    const ds = this.getDiscoverySections(id);
    if (ds) {
      const pushDiscoveryRows = (rows: unknown[], kindLabel: string, competitorName: string | null) => {
        if (!Array.isArray(rows)) {
          return;
        }
        for (const item of rows) {
          const q = this.promptFromDiscoveryItem(item);
          if (!q) {
            continue;
          }
          if (coveredQueries.has(this.normalizePromptQ(q))) {
            continue;
          }
          const answer_text =
            typeof item === 'object' && item && 'answer_text' in item
              ? String((item as any).answer_text || '')
              : '';
          const rawCit =
            typeof item === 'object' && item && Array.isArray((item as any).citations)
              ? (item as any).citations
              : [];
          const fromPages = citedByQuery.get(this.normalizePromptQ(q)) || [];
          const citations = this.mergeCitationChips(rawCit, fromPages);
          const { preview, needsMore } = this.responsePreviewLines(answer_text);
          const ai = idx++;
          out.push({
            trackKey: `${id}-${ai}`,
            accordionIndex: ai,
            platform: 'ai_mode',
            platformLabel: 'AI Mode',
            query: q,
            answer_text,
            kindLabel,
            competitorName,
            mentioned_brands: [],
            citations,
            preview,
            needsMore: needsMore || answer_text.length > preview.length
          });
        }
      };
      pushDiscoveryRows((ds['generic'] as unknown[]) || [], 'Generic', null);
      pushDiscoveryRows((ds['primary_branded'] as unknown[]) || [], 'Branded', null);
      const comps = ds['competitors'];
      if (comps && typeof comps === 'object') {
        for (const [name, rows] of Object.entries(comps as Record<string, unknown[]>)) {
          pushDiscoveryRows(Array.isArray(rows) ? rows : [], 'Competitor', name);
        }
      }
    }

    if (out.length === 0) {
      const queries = this.getQueries(id);
      if (queries && queries.length > 0) {
        const citedByQuery = this.buildCitedUrlsByQuery(id);
        queries.forEach((q, i) => {
          const fromPages = citedByQuery.get(this.normalizePromptQ(q)) || [];
          const { kindLabel, competitorName } = this.classifyPromptForDiscovery(id, q);
          const hint =
            'No stored response text for this prompt. Open sources below (if any) in a new tab.';
          out.push({
            trackKey: `${id}-q-${i}`,
            accordionIndex: i,
            platform: '—',
            platformLabel: '—',
            query: q,
            answer_text: hint,
            kindLabel,
            competitorName,
            mentioned_brands: [],
            citations: this.mergeCitationChips([], fromPages),
            preview: hint,
            needsMore: false
          });
        });
      }
    }

    return out;
  }

  /** Truncate response text for inline display. */
  truncateResponse(text: string, maxLen: number = 200): string {
    if (!text || text.length <= maxLen) return text ?? '';
    return text.slice(0, maxLen).trim() + '…';
  }

  /** Format platform keys (e.g. chatgpt, gemini) to display labels (ChatGPT, Gemini) for Discovery Citations. */
  getPlatformLabel(platformKey: string): string {
    return PLATFORM_LABELS[platformKey] ?? platformKey;
  }

  /** Return display labels for model_sources array for a cited page. */
  getPlatformLabels(sources: string[] | undefined): string[] {
    if (!Array.isArray(sources) || sources.length === 0) return [];
    return sources.map(s => this.getPlatformLabel(s));
  }

  /** Copy prompts, responses, citation links and cited pages for this citation. */
  copyCitedContent(citationId: number): void {
    const blocks: string[] = [];
    const items = this.getDiscoveryPromptAccordionItems(citationId);
    if (items.length > 0) {
      for (const it of items) {
        const parts = [
          `[${it.kindLabel}]${it.competitorName ? ' ' + it.competitorName : ''} (${it.platformLabel})`,
          `Prompt: ${it.query}`,
          `Response:\n${it.answer_text || '(empty)'}`
        ];
        if (it.citations.length) {
          parts.push(
            'Sources:\n' + it.citations.map((c) => `  [${c.chipIndex}] ${c.title} — ${c.url}`).join('\n')
          );
        }
        blocks.push(parts.join('\n'));
      }
    }
    const pages = this.getCitedPages(citationId);
    if (pages.length > 0) {
      const pageLines = pages.map((p: any) => {
        const parts = [`URL: ${p.url || ''}`];
        if (p.domain) parts.push(`Domain: ${p.domain}`);
        if (p.model_sources?.length) parts.push(`Sources: ${p.model_sources.join(', ')}`);
        const pqs = this.getPagePromptStrings(p);
        if (pqs.length) parts.push(`Prompts: ${pqs.join(' | ')}`);
        if (p.total_mentions?.length) parts.push(`Mentions: ${p.total_mentions.join(', ')}`);
        return parts.join('\n');
      });
      blocks.push('— Cited pages —\n\n' + pageLines.join('\n\n' + '-'.repeat(40) + '\n\n'));
    }
    if (blocks.length === 0) {
      this.snackBar.open('Nothing to copy yet', 'Close', { duration: 2000 });
      return;
    }
    const text = blocks.join('\n\n' + '='.repeat(80) + '\n\n');
    navigator.clipboard.writeText(text).then(() => {
      this.snackBar.open('Copied prompts, responses and links', 'Close', { duration: 2000 });
    }).catch(() => {
      this.snackBar.open('Failed to copy', 'Close', { duration: 3000 });
    });
  }

  /** Escape a value for CSV (wrap in quotes if contains comma, newline or quote; double internal quotes). */
  private escapeCsvValue(value: any): string {
    const s = value == null ? '' : String(value);
    if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  }

  /** Export this citation's summary, prompt responses and cited content as CSV. */
  exportCitationAsCsv(citation: DiscoveryCitation): void {
    const rows: string[] = [];
    const header = [
      'Section', 'ID', 'Brand', 'Category', 'Region', 'Base Domain', 'Cited Items', 'Last Fetched',
      'Platform', 'Prompt', 'Response', 'Mentions',
      'URL', 'Page Domain', 'Page Platforms', 'Page Prompts', 'Page Mentions'
    ];
    rows.push(header.map(h => this.escapeCsvValue(h)).join(','));

    rows.push([
      'Summary',
      this.escapeCsvValue(citation.id),
      this.escapeCsvValue(citation.brand),
      this.escapeCsvValue(citation.category),
      this.escapeCsvValue(citation.region),
      this.escapeCsvValue(citation.base_domain ?? ''),
      this.escapeCsvValue(citation.citation_count ?? ''),
      this.escapeCsvValue(this.formatDate(citation.last_fetched_at)),
      '', '', '', '', '', '', '', '', ''
    ].join(','));

    const promptResponses = this.getPromptResponses(citation.id);
    for (const pr of promptResponses) {
      const platformLabel = this.getPlatformLabel(pr.platform);
      const mentionsStr = Array.isArray(pr.mentioned_brands) ? pr.mentioned_brands.join('; ') : '';
      rows.push([
        'Prompt response',
        '', '', '', '', '', '', '',
        this.escapeCsvValue(platformLabel),
        this.escapeCsvValue(pr.query),
        this.escapeCsvValue(pr.answer_text),
        this.escapeCsvValue(mentionsStr),
        '', '', '', '', ''
      ].join(','));
    }

    const pages = this.getCitedPages(citation.id);
    for (const p of pages) {
      const platforms = Array.isArray(p.model_sources) ? this.getPlatformLabels(p.model_sources).join('; ') : '';
      const prompts = this.getPagePromptStrings(p).join(' | ');
      const mentions = Array.isArray(p.total_mentions) ? p.total_mentions.join('; ') : '';
      rows.push([
        'Cited page',
        '', '', '', '', '', '', '',
        '', '', '', '',
        this.escapeCsvValue(p.url ?? ''),
        this.escapeCsvValue(p.domain ?? ''),
        this.escapeCsvValue(platforms),
        this.escapeCsvValue(prompts),
        this.escapeCsvValue(mentions)
      ].join(','));
    }

    const csv = rows.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const safeBrand = (citation.brand || 'citation').replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 50);
    const date = new Date().toISOString().slice(0, 10);
    const filename = `discovery-citation-${safeBrand}-${citation.id}-${date}.csv`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    this.snackBar.open('Citation exported as CSV', 'Close', { duration: 2000 });
  }

  /** Export this citation's summary, prompt responses and cited content as a formatted TXT file. */
  exportCitationAsTxt(citation: DiscoveryCitation): void {
    const sep = '================================================================================';
    const line = '--------------------------------------------------------------------------------';
    const lines: string[] = [
      'DISCOVERY CITATION EXPORT',
      `Exported: ${new Date().toLocaleString()}`,
      '',
      sep,
      'SUMMARY',
      sep,
      `ID:              ${citation.id}`,
      `Brand:           ${citation.brand ?? ''}`,
      `Category:        ${citation.category ?? ''}`,
      `Region:          ${citation.region ?? ''}`,
      `Base Domain:     ${citation.base_domain ?? 'N/A'}`,
      `Cited Items:     ${citation.citation_count ?? 0}`,
      `Last Fetched:    ${this.formatDate(citation.last_fetched_at)}`,
      '',
    ];

    const promptResponses = this.getPromptResponses(citation.id);
    if (promptResponses.length > 0) {
      lines.push(sep);
      lines.push('PROMPT RESPONSES');
      lines.push(sep);
      lines.push('');
      promptResponses.forEach((pr, i) => {
        const platformLabel = this.getPlatformLabel(pr.platform);
        const mentionsStr = Array.isArray(pr.mentioned_brands) && pr.mentioned_brands.length > 0
          ? pr.mentioned_brands.join(', ')
          : 'None';
        lines.push(line);
        lines.push(`${i + 1}. ${platformLabel}`);
        lines.push('');
        lines.push(`Prompt:   ${(pr.query ?? '').replace(/\n/g, '\n           ')}`);
        lines.push('');
        lines.push(`Response: ${(pr.answer_text ?? '').replace(/\n/g, '\n          ')}`);
        lines.push('');
        lines.push(`Mentions: ${mentionsStr}`);
        lines.push('');
      });
    }

    const pages = this.getCitedPages(citation.id);
    if (pages.length > 0) {
      lines.push(sep);
      lines.push('CITED PAGES');
      lines.push(sep);
      lines.push('');
      pages.forEach((p, i) => {
        const platforms = Array.isArray(p.model_sources) ? this.getPlatformLabels(p.model_sources).join(', ') : '';
        const prompts = this.getPagePromptStrings(p).join(' | ');
        const mentions = Array.isArray(p.total_mentions) ? p.total_mentions.join(', ') : '';
        lines.push(`${i + 1}. ${p.url ?? ''}`);
        lines.push(`   Domain:    ${p.domain ?? 'N/A'}`);
        lines.push(`   Platforms: ${platforms || 'N/A'}`);
        lines.push(`   Prompts:   ${prompts || 'N/A'}`);
        lines.push(`   Mentions:  ${mentions || 'N/A'}`);
        lines.push('');
      });
    }

    const txt = lines.join('\n');
    const blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const safeBrand = (citation.brand || 'citation').replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 50);
    const date = new Date().toISOString().slice(0, 10);
    const filename = `discovery-citation-${safeBrand}-${citation.id}-${date}.txt`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    this.snackBar.open('Citation exported as TXT', 'Close', { duration: 2000 });
  }
}

@Component({
  selector: 'app-confirm-delete-citation-dialog',
  template: `
    <h2 mat-dialog-title>
      <mat-icon class="warning-icon">warning</mat-icon>
      Delete Discovery Citation?
    </h2>
    <mat-dialog-content>
      <p>This will remove stored AI visibility data for:</p>
      <div class="citation-info">
        <strong>Brand:</strong> {{ data.brand }}<br>
        <strong>Category:</strong> {{ data.category }}<br>
        <strong>Region:</strong> {{ data.region }}
      </div>
      <p class="hint">Next run for this brand will fetch fresh data.</p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cancel</button>
      <button mat-raised-button color="warn" [mat-dialog-close]="true" cdkFocusInitial>Delete</button>
    </mat-dialog-actions>
  `,
  styles: [`
    h2[mat-dialog-title] { display: flex; align-items: center; gap: 8px; }
    .warning-icon { color: #f44336; }
    .citation-info { padding: 12px; background: #f5f5f5; border-radius: 4px; margin: 12px 0; font-size: 14px; }
    .hint { font-size: 12px; color: #666; margin-top: 8px; }
  `],
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatDialogModule, MatIconModule, A11yModule]
})
export class ConfirmDeleteCitationDialogComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: { brand: string; category: string; region: string }) {}
}
